import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { FileText, FileDown, Loader2 } from "lucide-react";
import { api, fmt, fmtDate, apiError, downloadInvoicePdf } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PAY_STYLES = {
  PAID: "border-emerald-500/40 text-emerald-400",
  PARTIAL: "border-amber-500/40 text-amber-400",
  DUE: "border-amber-500/40 text-amber-400",
  DUE_ON_DELIVERY: "border-sky-500/40 text-sky-400",
  OVERDUE: "border-red-500/40 text-red-400",
  PENDING: "border-strong text-2",
};

export function PaymentChip({ ps }) {
  if (!ps) return null;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${PAY_STYLES[ps.status] || PAY_STYLES.PENDING}`}
      data-testid={`pay-chip-${ps.status.toLowerCase()}`}
    >
      {ps.label}
    </span>
  );
}

export default function AccountInvoicesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState(null);
  const [downloading, setDownloading] = useState("");

  useEffect(() => {
    if (!user) return;
    api.get("/invoices")
      .then(({ data }) => setInvoices(data))
      .catch((e) => { toast.error(apiError(e, "Couldn't load your invoices")); setInvoices([]); });
  }, [user]);

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6">
        <button onClick={() => navigate("/login", { state: { from: "/account/invoices" } })} className="rounded-full bg-amber-500 px-7 py-3.5 text-sm font-bold text-black" data-testid="invoices-login-cta">
          Sign in to view your invoices
        </button>
      </div>
    );
  }

  const download = async (inv) => {
    setDownloading(inv.id);
    try {
      await downloadInvoicePdf(inv.order_id);
    } catch (e) {
      toast.error(apiError(e, "Couldn't download the invoice"));
    } finally {
      setDownloading("");
    }
  };

  return (
    <div className="min-h-screen pb-24 pt-28" data-testid="invoices-page">
      <div className="mx-auto max-w-5xl px-6 lg:px-12">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="mb-3 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-amber-500">
            <span className="h-px w-10 bg-amber-500" />
            My Account
          </p>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-1 sm:text-4xl">Your Invoices</h1>
          <p className="mt-2 text-sm text-3">Every PAQVERO order comes with an invoice — download them anytime.</p>
        </motion.div>

        {invoices === null ? (
          <div className="mt-12 space-y-3" data-testid="invoices-loading">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex animate-pulse items-center gap-4 rounded-2xl border border-line bg-surface p-5 shadow-card">
                <div className="h-11 w-11 shrink-0 rounded-xl bg-hover" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-44 rounded bg-hover" />
                  <div className="h-3 w-32 rounded bg-hover" />
                </div>
                <div className="h-6 w-20 rounded-full bg-hover" />
                <div className="h-5 w-16 rounded bg-hover" />
                <div className="h-10 w-20 rounded-xl bg-hover" />
              </div>
            ))}
          </div>
        ) : invoices.length === 0 ? (
          <div className="mt-12 rounded-2xl border border-line bg-surface py-16 text-center" data-testid="invoices-empty">
            <FileText className="mx-auto h-8 w-8 text-3" />
            <p className="mt-3 text-sm text-3">No invoices yet — they'll appear here after your first order.</p>
            <button onClick={() => navigate("/shop")} className="mt-6 rounded-full bg-amber-500 px-7 py-3 text-sm font-bold text-black hover:bg-amber-400" data-testid="invoices-shop-cta">
              Shop PAQVERO
            </button>
          </div>
        ) : (
          <div className="mt-10 space-y-3">
            {invoices.map((inv) => (
              <motion.div
                key={inv.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-wrap items-center gap-4 rounded-2xl border border-line bg-surface p-5 shadow-card transition-shadow duration-300 hover:shadow-lift"
                data-testid={`invoice-row-${inv.invoice_number}`}
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-amber-500/25 bg-amber-500/[0.06]">
                  <FileText className="h-5 w-5 text-amber-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-mono text-sm font-bold text-1" data-testid={`invoice-number-${inv.invoice_number}`}>
                    {inv.invoice_number}
                    {inv.proforma && (
                      <span className="ml-2 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 font-sans text-[10px] font-bold uppercase tracking-wider text-amber-400"
                        data-testid={`invoice-proforma-badge-${inv.invoice_number}`}>Proforma</span>
                    )}
                  </p>
                  <p className="mt-0.5 text-xs text-3">
                    Order <button className="font-semibold text-amber-400 hover:underline" data-testid={`invoice-order-link-${inv.invoice_number}`} onClick={() => navigate(`/orders/${inv.order_id}`)}>{inv.order_number}</button>
                    {" · "}{fmtDate(inv.invoice_date)}
                    {inv.stamp && <span className="ml-2 font-bold uppercase text-red-400">· Order {inv.stamp}</span>}
                  </p>
                </div>
                <PaymentChip ps={inv.payment_status} />
                <span className="font-display text-lg font-semibold text-1">{fmt(inv.grand_total)}</span>
                <button
                  data-testid={`invoice-download-${inv.invoice_number}`}
                  onClick={() => download(inv)}
                  disabled={downloading === inv.id}
                  className="flex items-center gap-2 rounded-xl border border-strong px-4 py-2.5 text-xs font-bold text-1 transition-colors hover:border-amber-500/60 hover:text-amber-400 disabled:opacity-60"
                >
                  {downloading === inv.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />}
                  PDF
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
